import { useState, useEffect, useRef } from 'react'
import { validateProject, generateDocuments } from '../api/documents'
import { Button } from '../components/Button'
import type { ValidationError } from '../types'
import { getErrorMessage, getErrorSeverity } from '../utils/errorMessages'
import styles from './GenerateButton.module.css'

type GenState = 'idle' | 'validating' | 'generating' | 'success' | 'error'

interface GenerateButtonProps {
  projectId: string
  onSuccess: () => void
  onError?: (message: string) => void
}

const PROGRESS_MESSAGES = [
  'Calculando circuitos...',
  'Generando diagrama unifilar...',
  'Preparando memoria de cálculo...',
  'Creando formulario TE1...',
  'Finalizando documentos...',
]

interface ParsedError {
  message: string
  severity: 'blocking' | 'warning'
}

export function GenerateButton({ projectId, onSuccess, onError }: GenerateButtonProps) {
  const [state, setState] = useState<GenState>('idle')
  const [validationErrors, setValidationErrors] = useState<ParsedError[]>([])
  const [progressIndex, setProgressIndex] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (state === 'generating') {
      intervalRef.current = setInterval(() => {
        setProgressIndex((i) => (i + 1) % PROGRESS_MESSAGES.length)
      }, 4000)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [state])

  const handleGenerate = async () => {
    setState('validating')
    setValidationErrors([])

    try {
      const validation = await validateProject(projectId)

      if (!validation.is_valid) {
        const raw = (validation.errors ?? []) as unknown as ValidationError[]
        const parsed: ParsedError[] = raw.length > 0
          ? raw.map((e) => ({ message: getErrorMessage(e), severity: getErrorSeverity(e.code) }))
          : [{ message: 'El proyecto tiene errores que debes corregir antes de generar.', severity: 'blocking' }]
        setValidationErrors(parsed)
        setState('error')
        return
      }
    } catch {
      setState('error')
      setValidationErrors([{ message: 'No pudimos validar el proyecto. Verifica tu conexión e intenta nuevamente.', severity: 'blocking' }])
      return
    }

    setState('generating')
    setProgressIndex(0)

    try {
      await generateDocuments(projectId)
      setState('success')
      setTimeout(() => {
        onSuccess()
      }, 1500)
    } catch {
      setState('error')
      setValidationErrors([{ message: 'No pudimos generar los documentos. Intenta nuevamente.', severity: 'blocking' }])
      onError?.('No pudimos generar los documentos. Intenta nuevamente.')
    }
  }

  const handleRetry = () => {
    setState('idle')
    setValidationErrors([])
  }

  if (state === 'idle') {
    return (
      <Button onClick={handleGenerate} fullWidth>
        Generar documentos
      </Button>
    )
  }

  if (state === 'validating') {
    return (
      <div className={styles.box}>
        <div className={styles.spinnerRow}>
          <span className={styles.spinner} aria-hidden="true" />
          <span className={styles.statusText}>Validando normativa RIC...</span>
        </div>
      </div>
    )
  }

  if (state === 'generating') {
    return (
      <div className={styles.box}>
        <div className={styles.spinnerRow}>
          <span className={styles.spinner} aria-hidden="true" />
          <span className={styles.statusText}>{PROGRESS_MESSAGES[progressIndex]}</span>
        </div>
        <div className={styles.progressBar}>
          <div className={styles.progressIndeterminate} />
        </div>
        <p className={styles.eta}>Esto toma aproximadamente 30 segundos</p>
      </div>
    )
  }

  if (state === 'success') {
    return (
      <div className={styles.box}>
        <div className={styles.successIcon} aria-label="Éxito">✓</div>
        <p className={styles.successText}>¡Documentos generados exitosamente!</p>
      </div>
    )
  }

  // error state
  return (
    <div className={styles.box}>
      <p className={styles.errorTitle}>Corrige los siguientes problemas antes de generar:</p>
      <ul className={styles.errorList}>
        {validationErrors.map((e, i) => (
          <li
            key={i}
            className={[
              styles.errorItem,
              e.severity === 'warning' ? styles.errorItemWarning : styles.errorItemBlocking,
            ].join(' ')}
          >
            <span aria-hidden="true">{e.severity === 'warning' ? '⚠️' : '🚫'}</span> {e.message}
          </li>
        ))}
      </ul>
      <Button variant="secondary" onClick={handleRetry} fullWidth>
        Corregir y volver
      </Button>
    </div>
  )
}
